import { UIText3 } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { BubblesContainer } from './containers/BubblesContainer';
import { ChatMessageMetaContainer } from './containers/ChatMessageMetaContainer';
import { ImageMessageContainer } from './containers/ImageMessageContainer';
import { TextMessageContainer } from './containers/TextMessageContainer';
import { useMessageContextMenu, useMessageType, useUserColor } from './hooks';
import { trackShowMessage } from './tracking';
import { ChatMessageChatbotGreeting } from '../../components/ChatMessageChatbotGreeting';
import { ChatMessageLayout } from '../../components/ChatMessageLayout';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { TMessage, TOutcomingStatus } from '../../types/messages';
import { ChatMessageOfferGalleryContainer } from '../ChatMessageOfferGalleryContainer';
import { DocumentMessageContainer } from '../DocumentMessageContainer';
import { ImageAsDocumentMessageContainer } from '../ImageAsDocumentMessageContainer';
import { OfferMessageContainer } from '../OfferMessageContainer';
import { ActionButtonsContainer } from './containers/ActionButtonsContainer';
import { ErrorSignContainer } from './containers/ErrorSignContainer/ErrorSignContainer';
import { UserNameContainer } from './containers/UserNameContainer/UserNameContainer';
import { useMessageContentType } from './hooks/useMessageContentType';
import { ChatMessageContent } from '../../components/ChatMessageContent';

type TChatMessageContainerProps = {
  message: TMessage & { status: TOutcomingStatus };
  messageId: string;
  isLastInChain: boolean;
  isFirstUserPhrase: boolean;
};

export const ChatMessageContainer: React.FC<TChatMessageContainerProps> = ({
  message,
  messageId,
  isLastInChain,
  isFirstUserPhrase,
}) => {
  const chatId = useSelector(currentOpenChatIDSelector) || '';
  const { status, isOutcoming, communicationId, userId, isDeleted } = message;
  const isOfferMessage = Boolean(message?.offers?.length);
  const isChatbotOfferMessage = chatId === CHATBOT_ID && isOfferMessage && !isOutcoming;

  const messageRef = React.useRef<HTMLDivElement | null>(null);

  useMessageContextMenu({ elementRef: messageRef, messageId, isDeleted });

  const onShow = React.useCallback(() => {
    const attributes = isChatbotOfferMessage ? 'Horizontal_scroll ' : undefined;

    trackShowMessage({ chatId, messageId, communicationId, attributes, offer: message?.offers });
  }, [chatId, messageId, communicationId, isChatbotOfferMessage, message?.offers]);

  const contentType = useMessageContentType({ message, messageId });

  const messageType = useMessageType(message);

  const userColor = useUserColor(userId);

  if (message.type === 'textAndImages') {
    return (
      <ChatMessageLayout messageType={messageType} messageStatus={status} onShow={onShow}>
        <ChatMessageChatbotGreeting
          message={message}
          isLastInChain={isLastInChain}
          messageId={messageId}
          ref={messageRef}
        />
      </ChatMessageLayout>
    );
  }

  if (message.offers && message.offers.length > 1) {
    return (
      <ChatMessageLayout messageType={messageType} messageStatus={status} onShow={onShow}>
        <ChatMessageOfferGalleryContainer messageId={messageId} message={message} ref={messageRef} />
      </ChatMessageLayout>
    );
  }

  return (
    <ChatMessageLayout messageType={messageType} messageStatus={status} onShow={onShow}>
      <ChatMessageContent
        ref={messageRef}
        contentType={contentType}
        messageType={messageType}
        isLastMessageInChain={isLastInChain}
        errorText={
          status === ERequestApiStatus.Failed ? <UIText3 color="text-negative-default">не отправлено</UIText3> : null
        }
        errorSign={<ErrorSignContainer message={message} messageId={messageId} />}
        bubbles={<BubblesContainer message={message} messageId={messageId} />}
        offerMessage={<OfferMessageContainer message={message} messageId={messageId} />}
        documentMessage={<DocumentMessageContainer message={message} messageId={messageId} />}
        imageAsDocumentMessage={<ImageAsDocumentMessageContainer message={message} messageId={messageId} />}
        imageMessage={<ImageMessageContainer message={message} messageId={messageId} />}
        textMessage={<TextMessageContainer message={message} messageId={messageId} />}
        userName={<UserNameContainer isFirstUserPhrase={isFirstUserPhrase} messageUserId={userId} />}
        actionButtons={<ActionButtonsContainer message={message} messageId={messageId} />}
        messageMeta={<ChatMessageMetaContainer message={message} />}
        userColor={userColor}
        deletedMessage={message.message}
      />
    </ChatMessageLayout>
  );
};
